import { useEffect, useRef } from "react";

import { audioService } from "@/core/audio/audioService";
import { useBingoGameStore } from "@/plugins/bingo/state/bingoGameStore";

import BingoCheckOverlay from "./BingoCheckOverlay";

/**
 * BingoCheckLiveOverlay — projector-facing wrapper around
 * BingoCheckOverlay that reads verification state from the game
 * store and drives the heartbeat / confirmation audio.
 *
 * Owns audio lifecycle so it stays isolated from the host window
 * (only the Projector plays sound — same pattern as ball reveal).
 *
 * Mount this once inside ProjectorView.
 */
export default function BingoCheckLiveOverlay() {
  const bingoCheck = useBingoGameStore((s) => s.bingoCheck);
  const prevVerifiedRef = useRef(0);
  const prevBpmRef = useRef(60);
  const prevActiveRef = useRef(false);

  // Start / stop the heartbeat as verification opens and closes.
  useEffect(() => {
    const isActive = !!bingoCheck;
    const wasActive = prevActiveRef.current;

    if (isActive && !wasActive) {
      // Verification just opened — start the calm heartbeat.
      audioService.startHeartbeat(bingoCheck.bpm || 60);
      prevBpmRef.current = bingoCheck.bpm || 60;
      prevVerifiedRef.current = 0;
    } else if (!isActive && wasActive) {
      // Verification closed — stop any lingering heartbeat.
      audioService.stopHeartbeat();
      prevVerifiedRef.current = 0;
      prevBpmRef.current = 60;
    }

    prevActiveRef.current = isActive;
  }, [bingoCheck]);

  // React to verifiedCount / bpm changes: play confirm chime and
  // adjust cadence. When the FINAL number is confirmed, stop the
  // heartbeat instantly (signature "cut to silence" moment).
  useEffect(() => {
    if (!bingoCheck) return;
    const { verifiedCount, numbers, bpm } = bingoCheck;
    const prev = prevVerifiedRef.current;
    if (verifiedCount > prev) {
      const isFinal = verifiedCount === numbers.length;
      audioService.playCheckConfirm(isFinal);
      if (isFinal) {
        audioService.stopHeartbeat();
      } else if (bpm !== prevBpmRef.current) {
        audioService.setHeartbeatBpm(bpm);
        prevBpmRef.current = bpm;
      }
    }
    prevVerifiedRef.current = verifiedCount;
  }, [bingoCheck]);

  return (
    <BingoCheckOverlay
      active={!!bingoCheck}
      numbers={bingoCheck?.numbers || []}
      verifiedCount={bingoCheck?.verifiedCount || 0}
      heartbeatBpm={bingoCheck?.bpm || 60}
      playerLabel={bingoCheck?.playerLabel || "Claimed Line"}
      testidPrefix="bingo-check-live"
    />
  );
}
